/* Customer Account API auth + API helpers (theme-safe, public client with PKCE) */

(() => {
const STORAGE_KEYS = {
  state: 'ca_state',
  verifier: 'ca_verifier',
  token: 'ca_token',
  tokenExpiry: 'ca_token_expiry',
  refreshToken: 'ca_refresh_token',
};

function base64UrlEncode(bytes) {
  const bin = Array.from(bytes, (b) => String.fromCharCode(b)).join('');
  const b64 = btoa(bin);
  return b64.replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/g, '');
}

function randomBase64Url(bytesLength) {
  const bytes = new Uint8Array(bytesLength);
  crypto.getRandomValues(bytes);
  return base64UrlEncode(bytes);
}

async function sha256Base64Url(str) {
  const data = new TextEncoder().encode(str);
  const digest = await crypto.subtle.digest('SHA-256', data);
  return base64UrlEncode(new Uint8Array(digest));
}

async function fetchJson(url, init) {
  const res = await fetch(url, init);
  if (!res.ok) {
    const text = await res.text().catch(() => '');
    throw new Error(`Request failed ${res.status}: ${text || res.statusText}`);
  }
  return await res.json();
}

async function getOpenIdConfiguration(storefrontDomain) {
  return await fetchJson(`https://${storefrontDomain}/.well-known/openid-configuration`);
}

async function getCustomerAccountApiConfiguration(storefrontDomain) {
  // Returns API endpoints including GraphQL endpoint
  return await fetchJson(`https://${storefrontDomain}/.well-known/customer-account-api`);
}

function setSessionItem(key, value) {
  sessionStorage.setItem(key, value);
}

function getSessionItem(key) {
  return sessionStorage.getItem(key);
}

function clearSessionItem(key) {
  sessionStorage.removeItem(key);
}

function setToken(tokenData) {
  const accessToken = tokenData?.access_token || tokenData?.accessToken;
  if (!accessToken || typeof accessToken !== 'string' || !accessToken.startsWith('shcat_')) {
    throw new Error('Invalid customer access token returned from OAuth.');
  }

  setSessionItem(STORAGE_KEYS.token, accessToken);

  const refreshToken = tokenData?.refresh_token || tokenData?.refreshToken;
  if (refreshToken) {
    setSessionItem(STORAGE_KEYS.refreshToken, refreshToken);
  }

  const expiresIn = Number(tokenData?.expires_in || tokenData?.expiresIn || 0);
  if (expiresIn > 0) {
    const expiryMs = Date.now() + expiresIn * 1000;
    setSessionItem(STORAGE_KEYS.tokenExpiry, String(expiryMs));
  } else {
    clearSessionItem(STORAGE_KEYS.tokenExpiry);
  }
}

function getToken() {
  const token = getSessionItem(STORAGE_KEYS.token);
  if (!token || token === 'undefined' || token === 'null' || !token.startsWith('shcat_')) return null;
  const expiry = getSessionItem(STORAGE_KEYS.tokenExpiry);
  if (expiry && Date.now() > Number(expiry)) return null;
  return token;
}

function clearToken() {
  clearSessionItem(STORAGE_KEYS.token);
  clearSessionItem(STORAGE_KEYS.tokenExpiry);
  clearSessionItem(STORAGE_KEYS.refreshToken);
}

function getRefreshToken() {
  return getSessionItem(STORAGE_KEYS.refreshToken);
}

async function refreshCustomerAccessToken({ storefrontDomain, clientId }) {
  const refreshToken = getRefreshToken();
  if (!refreshToken) throw new Error('Missing refresh token.');

  const openid = await getOpenIdConfiguration(storefrontDomain);
  const tokenEndpoint = openid.token_endpoint;
  const body = new URLSearchParams({
    grant_type: 'refresh_token',
    client_id: clientId,
    refresh_token: refreshToken,
  });

  const res = await fetch(tokenEndpoint, {
    method: 'POST',
    headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
    body,
  });

  if (!res.ok) {
    clearToken();
    const text = await res.text().catch(() => '');
    throw new Error(`Token refresh failed (${res.status}). ${text}`);
  }

  const tokenData = await res.json();
  setToken(tokenData);
  return tokenData;
}

async function startCustomerAccountLogin({
  storefrontDomain,
  clientId,
  redirectUri,
  scope = 'openid email customer-account-api:full',
  locale,
  regionCountry,
}) {
  if (!storefrontDomain) throw new Error('Missing storefrontDomain');
  if (!clientId) throw new Error('Missing clientId');
  if (!redirectUri) throw new Error('Missing redirectUri');

  const openid = await getOpenIdConfiguration(storefrontDomain);
  const authorizationEndpoint = openid.authorization_endpoint;

  const state = randomBase64Url(16);
  const verifier = randomBase64Url(32);
  const challenge = await sha256Base64Url(verifier);

  setSessionItem(STORAGE_KEYS.state, state);
  setSessionItem(STORAGE_KEYS.verifier, verifier);

  const url = new URL(authorizationEndpoint);
  url.searchParams.set('client_id', clientId);
  url.searchParams.set('response_type', 'code');
  url.searchParams.set('redirect_uri', redirectUri);
  url.searchParams.set('scope', scope);
  url.searchParams.set('state', state);
  url.searchParams.set('code_challenge', challenge);
  url.searchParams.set('code_challenge_method', 'S256');
  if (locale) url.searchParams.set('locale', locale);
  if (regionCountry) url.searchParams.set('region_country', regionCountry);

  window.location.assign(url.toString());
}

async function handleCustomerAccountCallback({
  storefrontDomain,
  clientId,
  redirectUri,
}) {
  const url = new URL(window.location.href);
  const code = url.searchParams.get('code');
  const returnedState = url.searchParams.get('state');
  const error = url.searchParams.get('error');
  const errorDescription = url.searchParams.get('error_description');

  if (error) {
    throw new Error(errorDescription || error);
  }

  if (!code || !returnedState) {
    throw new Error('Missing code or state.');
  }

  const expectedState = getSessionItem(STORAGE_KEYS.state);
  const verifier = getSessionItem(STORAGE_KEYS.verifier);

  if (!expectedState || !verifier || returnedState !== expectedState) {
    throw new Error('Invalid or expired login session. Please try again.');
  }

  const openid = await getOpenIdConfiguration(storefrontDomain);
  const tokenEndpoint = openid.token_endpoint;

  const body = new URLSearchParams({
    grant_type: 'authorization_code',
    client_id: clientId,
    redirect_uri: redirectUri,
    code,
    code_verifier: verifier,
  });

  const res = await fetch(tokenEndpoint, {
    method: 'POST',
    headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
    body,
  });

  if (!res.ok) {
    const text = await res.text().catch(() => '');
    throw new Error(`Token exchange failed (${res.status}). ${text}`);
  }

  const tokenData = await res.json();
  setToken(tokenData);

  // Clean up one-time PKCE values
  clearSessionItem(STORAGE_KEYS.state);
  clearSessionItem(STORAGE_KEYS.verifier);

  return tokenData;
}

async function customerAccountGraphql({
  storefrontDomain,
  clientId,
  apiVersion = 'latest',
  query,
  variables,
}) {
  const token = getToken();
  if (!token) throw new Error('Not logged in.');

  const cfg = await getCustomerAccountApiConfiguration(storefrontDomain);

  // The .well-known response shape can vary; support a couple common patterns.
  const graphqlUrl =
    cfg?.graphql_api ||
    cfg?.endpoints?.graphql ||
    cfg?.customer_account_api?.graphql_endpoint ||
    cfg?.graphql_endpoint ||
    cfg?.graphql?.endpoint;

  if (!graphqlUrl) {
    throw new Error('Missing Customer Account API GraphQL endpoint from discovery.');
  }

  const endpoint = graphqlUrl
    .replace('{api_version}', apiVersion)
    .replace('{version}', apiVersion);

  let res = await fetch(endpoint, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: token,
    },
    body: JSON.stringify({ query, variables: variables || {} }),
  });

  if (res.status === 401 && clientId && getRefreshToken()) {
    await refreshCustomerAccessToken({ storefrontDomain, clientId });
    const retryToken = getToken();
    res = await fetch(endpoint, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: retryToken,
      },
      body: JSON.stringify({ query, variables: variables || {} }),
    });
  }

  const json = await res.json().catch(() => null);
  if (!res.ok) {
    const message = json?.errors?.[0]?.message || `Customer API request failed (${res.status}).`;
    throw new Error(message);
  }
  if (json?.errors?.length) {
    throw new Error(json.errors[0]?.message || 'Customer API error');
  }
  return json?.data;
}

async function loadCustomerOverview({ storefrontDomain, clientId }) {
  const data = await customerAccountGraphql({
    storefrontDomain,
    clientId,
    query: `
      query CustomerOverview {
        customer {
          firstName
          lastName
          emailAddress {
            emailAddress
          }
          phoneNumber {
            phoneNumber
          }
          defaultAddress {
            id
            address1
            address2
            city
            province
            zip
            country
          }
          addresses(first: 20) {
            nodes {
              id
              address1
              address2
              city
              province
              zip
              country
            }
          }
          orders(first: 20, reverse: true) {
            nodes {
              id
              name
              processedAt
              financialStatus
              fulfillmentStatus
              totalPrice {
                amount
                currencyCode
              }
              statusPageUrl
            }
          }
        }
      }
    `,
  });

  return data?.customer;
}

async function updateCustomerOverview({ storefrontDomain, clientId, firstName, lastName }) {
  const data = await customerAccountGraphql({
    storefrontDomain,
    clientId,
    query: `
      mutation UpdateCustomer($input: CustomerUpdateInput!) {
        customerUpdate(input: $input) {
          customer {
            firstName
            lastName
            phoneNumber {
              phoneNumber
            }
          }
          userErrors {
            field
            message
          }
        }
      }
    `,
    variables: {
      input: {
        firstName,
        lastName,
      },
    },
  });

  const result = data?.customerUpdate;
  if (result?.userErrors?.length) {
    throw new Error(result.userErrors[0]?.message || 'Could not update profile.');
  }
  return result?.customer;
}

async function createCustomerAddress({
  storefrontDomain,
  clientId,
  address1,
  address2,
  city,
  zoneCode,
  zip,
  territoryCode,
}) {
  const data = await customerAccountGraphql({
    storefrontDomain,
    clientId,
    query: `
      mutation CreateCustomerAddress($address: CustomerAddressInput!) {
        customerAddressCreate(address: $address) {
          customerAddress {
            id
            address1
            address2
            city
            zoneCode
            zip
            territoryCode
          }
          userErrors {
            field
            message
          }
        }
      }
    `,
    variables: {
      address: {
        address1,
        address2,
        city,
        zoneCode,
        zip,
        territoryCode,
      },
    },
  });

  const result = data?.customerAddressCreate;
  if (result?.userErrors?.length) {
    throw new Error(result.userErrors[0]?.message || 'Could not add address.');
  }
  return result?.customerAddress;
}

async function setDefaultCustomerAddress({ storefrontDomain, clientId, addressId }) {
  const data = await customerAccountGraphql({
    storefrontDomain,
    clientId,
    query: `
      mutation SetDefaultCustomerAddress($addressId: ID!) {
        customerAddressUpdate(addressId: $addressId, defaultAddress: true) {
          customerAddress {
            id
          }
          userErrors {
            field
            message
          }
        }
      }
    `,
    variables: { addressId },
  });

  const result = data?.customerAddressUpdate;
  if (result?.userErrors?.length) {
    throw new Error(result.userErrors[0]?.message || 'Could not set default address.');
  }
  return result?.customerAddress?.id;
}

async function updateCustomerAddress({
  storefrontDomain,
  clientId,
  addressId,
  address1,
  address2,
  city,
  zoneCode,
  zip,
  territoryCode,
}) {
  const data = await customerAccountGraphql({
    storefrontDomain,
    clientId,
    query: `
      mutation UpdateCustomerAddress($addressId: ID!, $address: CustomerAddressInput!, $defaultAddress: Boolean) {
        customerAddressUpdate(addressId: $addressId, address: $address, defaultAddress: $defaultAddress) {
          customerAddress {
            id
          }
          userErrors {
            field
            message
          }
        }
      }
    `,
    variables: {
      addressId,
      defaultAddress: false,
      address: {
        address1,
        address2,
        city,
        zoneCode,
        zip,
        territoryCode,
      },
    },
  });

  const result = data?.customerAddressUpdate;
  if (result?.userErrors?.length) {
    throw new Error(result.userErrors[0]?.message || 'Could not update address.');
  }
  return result?.customerAddress;
}

async function deleteCustomerAddress({ storefrontDomain, clientId, customerAddressId }) {
  const data = await customerAccountGraphql({
    storefrontDomain,
    clientId,
    query: `
      mutation AddressDelete($id: ID!) {
        customerAddressDelete(addressId: $id) {
          deletedAddressId
          userErrors {
            field
            message
          }
        }
      }
    `,
    variables: { id: customerAddressId },
  });

  const result = data?.customerAddressDelete;
  if (result?.userErrors?.length) {
    throw new Error(result.userErrors[0]?.message || 'Could not delete address.');
  }
  return result?.deletedAddressId;
}

async function logoutCustomerAccount({ storefrontDomain, postLogoutRedirectUri }) {
  clearToken();
  // For Customer Account API browser flow we keep logout local.
  // Shopify end_session endpoint expects id_token and can fail with "Invalid id_token".
  window.location.assign(postLogoutRedirectUri || '/');
}

function isLoggedIn() {
  return Boolean(getToken());
}

window.CustomerAccount = {
  startCustomerAccountLogin,
  handleCustomerAccountCallback,
  customerAccountGraphql,
  loadCustomerOverview,
  updateCustomerOverview,
  createCustomerAddress,
  setDefaultCustomerAddress,
  updateCustomerAddress,
  deleteCustomerAddress,
  refreshCustomerAccessToken,
  logoutCustomerAccount,
  isLoggedIn,
};
})();

